import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Dispatch } from '../../../store/store';
import InteractiveButton from '../../../components/InteractiveButton/InteractiveButton';
import { ButtonType } from '../../../types/button-type';

const POLL_INTERVAL_MS = 5000;

type ComponentProps = ReturnType<typeof mapDispatch>;

const ReconciliationBanner: React.FC<ComponentProps> = ({
  getSetupReconciliationStatus,
  stopSetupReconciliation,
}) => {
  const { id } = useParams();
  const [nextCheckAt, setNextCheckAt] = useState<number | null>(null);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const [isStopping, setIsStopping] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      const status = await getSetupReconciliationStatus(id);
      if (cancelled) return;
      setNextCheckAt(status?.active && status.nextCheckAt ? new Date(status.nextCheckAt).getTime() : null);
      setLastError(status?.active ? status.lastError ?? null : null);
    };

    poll();
    const intervalId = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [id]);

  useEffect(() => {
    if (nextCheckAt === null) {
      setSecondsRemaining(0);
      return;
    }

    const tick = () => setSecondsRemaining(Math.max(0, Math.round((nextCheckAt - Date.now()) / 1000)));
    tick();
    const intervalId = setInterval(tick, 1000);

    return () => clearInterval(intervalId);
  }, [nextCheckAt]);

  if (nextCheckAt === null) return null;

  const onStopHandler = async () => {
    setIsStopping(true);
    const succeeded = await stopSetupReconciliation(id);
    setIsStopping(false);

    if (succeeded) {
      setNextCheckAt(null);
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-sm border border-warning bg-warning/10 px-4 py-3 text-sm text-black dark:text-white">
      <span>
        The system is busy updating all the controller settings. Re-checking in {secondsRemaining}s.
        {lastError && (
          <span className="mt-1 block text-danger">Last attempt failed: {lastError}</span>
        )}
      </span>
      <InteractiveButton
        text={isStopping ? 'Stopping...' : 'Stop'}
        type={ButtonType.PrimaryInverse}
        onClick={onStopHandler}
        isLoading={isStopping}
      />
    </div>
  );
};

const mapDispatch = (dispatch: Dispatch) => ({
  getSetupReconciliationStatus: dispatch.controllers.getSetupReconciliationStatus,
  stopSetupReconciliation: dispatch.controllers.stopSetupReconciliation,
});

export default connect(null, mapDispatch)(ReconciliationBanner);
